import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronUp, ChevronDown, Plus, X, Eye, Pencil, ArrowLeft, Wand2, Copy, Trash2, Type } from "lucide-react";
import { toast } from "sonner";

const API = '/api';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [components, setComponents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    const [tRes, cRes] = await Promise.all([
      axios.get(`${API}/templates/`),
      axios.get(`${API}/components/`),
    ]);
    setTemplates(tRes.data.templates);
    setComponents(cRes.data.components);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const startEdit = (tpl) => {
    setEditing({ ...tpl, layers: [...tpl.layers] });
    setPreview(null);
  };

  const moveLayer = (idx, dir) => {
    const layers = [...editing.layers];
    const swap = idx + dir;
    if (swap < 0 || swap >= layers.length) return;
    [layers[idx], layers[swap]] = [layers[swap], layers[idx]];
    setEditing({ ...editing, layers });
  };

  const removeLayer = (idx) => {
    const layers = editing.layers.filter((_, i) => i !== idx);
    setEditing({ ...editing, layers });
  };

  const addLayer = (compId) => {
    setEditing({ ...editing, layers: [...editing.layers, compId] });
  };

  const saveTemplate = async () => {
    await axios.put(`${API}/templates/${editing.id}`, {
      name: editing.name,
      mode: editing.mode,
      layers: editing.layers,
    });
    setEditing(null);
    fetchData();
  };

  const loadPreview = async (tplId) => {
    const resp = await axios.post(`${API}/processor/test`, {
      from_address: "alice@acme.com",
      to: ["demo@external.com"],
      subject: "Preview Email",
      headers: {},
      body: "<p>Preview body</p>",
      body_type: "html",
    });
    setPreview(resp.data.signature_html);
  };

  const duplicateTemplate = async (tplId) => {
    try {
      await axios.post(`${API}/templates/${tplId}/duplicate`);
      toast.success("Template duplicated");
      fetchData();
    } catch (e) {
      toast.error("Duplicate failed");
    }
  };

  const deleteTemplate = async (tplId) => {
    try {
      await axios.delete(`${API}/templates/${tplId}`);
      toast.success("Template deleted");
      setDeleteConfirm(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const startRename = (tpl) => {
    setRenaming(tpl.id);
    setRenameValue(tpl.name);
    setDeleteConfirm(null);
  };

  const saveRename = async (tplId) => {
    if (!renameValue.trim()) return;
    try {
      await axios.put(`${API}/templates/${tplId}`, { name: renameValue.trim() });
      toast.success("Template renamed");
      setRenaming(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    }
  };

  const compMap = {};
  components.forEach((c) => { compMap[c.id] = c; });

  if (loading) {
    return <div className="p-8 text-sm text-[#5E5E5E]">Loading templates...</div>;
  }

  // Editor view
  if (editing) {
    const available = components.filter((c) => !editing.layers.includes(c.id));
    return (
      <div className="p-8 max-w-[1600px]" data-testid="template-editor">
        <button onClick={() => setEditing(null)} className="flex items-center gap-1 text-sm text-[#0F6CBD] hover:underline mb-4" data-testid="back-to-list-btn">
          <ArrowLeft size={14} /> Back to templates
        </button>
        <div className="flex items-center gap-3 mb-6">
          <Input
            value={editing.name}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            className="text-xl font-heading font-semibold border-[#E1DFDD] rounded-sm max-w-md"
            data-testid="template-name-input"
          />
          <select
            value={editing.mode}
            onChange={(e) => setEditing({ ...editing, mode: e.target.value })}
            className="border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm"
            data-testid="template-mode-select"
          >
            <option value="FULL">FULL</option>
            <option value="SHORT">SHORT</option>
          </select>
          <Button onClick={saveTemplate} className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm ml-auto" data-testid="save-template-btn">
            Save Template
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#E1DFDD] rounded-sm">
          {/* Layers panel */}
          <div className="col-span-1 lg:col-span-4 border-r border-[#E1DFDD] bg-[#FAFAFA]">
            <div className="px-4 py-3 border-b border-[#E1DFDD]">
              <h3 className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">
                Layers ({editing.layers.length})
              </h3>
            </div>
            <div className="p-3 space-y-1 max-h-[60vh] overflow-y-auto">
              {editing.layers.map((layerId, idx) => {
                const comp = compMap[layerId];
                return (
                  <div
                    key={`${layerId}-${idx}`}
                    className="flex items-center gap-2 bg-white border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm"
                    data-testid={`layer-${idx}`}
                  >
                    <span className="text-[10px] font-mono text-[#5E5E5E] w-4">{idx + 1}</span>
                    <span className="flex-1 truncate text-[#171717]">
                      {comp ? comp.name : layerId}
                    </span>
                    <span className="text-[10px] font-mono text-[#0F6CBD]">{comp?.type}</span>
                    <button onClick={() => moveLayer(idx, -1)} disabled={idx === 0} className="text-[#5E5E5E] hover:text-[#171717] disabled:opacity-30">
                      <ChevronUp size={14} />
                    </button>
                    <button onClick={() => moveLayer(idx, 1)} disabled={idx === editing.layers.length - 1} className="text-[#5E5E5E] hover:text-[#171717] disabled:opacity-30">
                      <ChevronDown size={14} />
                    </button>
                    <button onClick={() => removeLayer(idx)} className="text-[#A4262C] hover:text-[#A4262C]/80">
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
            {available.length > 0 && (
              <div className="p-3 border-t border-[#E1DFDD]">
                <p className="text-[10px] uppercase tracking-wider text-[#5E5E5E] font-semibold mb-2">Add Component</p>
                <div className="space-y-1">
                  {available.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => addLayer(c.id)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[#5E5E5E] hover:bg-white hover:text-[#171717] rounded-sm transition-colors"
                      data-testid={`add-component-${c.id}`}
                    >
                      <Plus size={12} />
                      <span className="truncate">{c.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Preview panel */}
          <div className="col-span-1 lg:col-span-8 bg-white p-8 min-h-[60vh]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">Preview</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadPreview(editing.id)}
                className="text-xs border-[#E1DFDD] rounded-sm"
                data-testid="load-preview-btn"
              >
                <Eye size={12} className="mr-1" /> Load Preview
              </Button>
            </div>
            {preview ? (
              <div
                className="border border-[#E1DFDD] rounded-sm p-6 bg-[#FAFAFA]"
                data-testid="signature-preview-html"
                dangerouslySetInnerHTML={{ __html: preview }}
              />
            ) : (
              <div className="border border-dashed border-[#E1DFDD] rounded-sm p-8 text-center text-sm text-[#5E5E5E]">
                Click "Load Preview" to see the rendered signature
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="p-8 max-w-[1600px]" data-testid="templates-page">
      <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Templates</h1>
      <p className="text-sm text-[#5E5E5E] mt-1">Manage signature templates and their component layers</p>

      <div className="mt-4">
        <Button onClick={() => navigate("/builder")} className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm" data-testid="open-builder-btn">
          <Wand2 size={14} className="mr-2" /> New Template (Builder)
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {templates.map((tpl) => (
          <div key={tpl.id} className="bg-white border border-[#E1DFDD] rounded-sm hover:border-[#0F6CBD] transition-colors" data-testid={`template-card-${tpl.id}`}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                {renaming === tpl.id ? (
                  <div className="flex items-center gap-2 flex-1 mr-2">
                    <Input value={renameValue} onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") saveRename(tpl.id); if (e.key === "Escape") setRenaming(null); }}
                      className="text-sm font-medium border-[#0F6CBD] rounded-sm h-8" autoFocus
                      data-testid={`rename-input-${tpl.id}`} />
                    <Button size="sm" onClick={() => saveRename(tpl.id)}
                      className="text-xs bg-[#0F6CBD] text-white rounded-sm h-8 px-3" data-testid={`rename-save-${tpl.id}`}>Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setRenaming(null)}
                      className="text-xs rounded-sm h-8 px-2">Cancel</Button>
                  </div>
                ) : (
                  <h3 className="font-heading text-base font-medium text-[#171717]">{tpl.name}</h3>
                )}
                <span className={`px-2 py-0.5 rounded-sm text-xs font-medium shrink-0 ${tpl.mode === "FULL" ? "bg-[#0F6CBD] text-white" : "bg-[#D83B01] text-white"}`}>
                  {tpl.mode}
                </span>
              </div>
              <p className="text-xs text-[#5E5E5E]">{tpl.layers.length} layers &middot; {tpl.language?.toUpperCase() || "EN"}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {tpl.layers.slice(0, 4).map((lid) => (
                  <span key={lid} className="text-[10px] bg-[#F3F2F1] text-[#5E5E5E] px-1.5 py-0.5 rounded-sm">
                    {compMap[lid]?.type || lid}
                  </span>
                ))}
                {tpl.layers.length > 4 && (
                  <span className="text-[10px] text-[#5E5E5E]">+{tpl.layers.length - 4} more</span>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#E1DFDD] flex gap-2">
              <Button variant="outline" size="sm"
                onClick={() => navigate(`/builder?edit=${tpl.id}`)}
                className="text-xs border-[#E1DFDD] rounded-sm"
                data-testid={`edit-template-${tpl.id}`}>
                <Pencil size={12} className="mr-1" /> Edit
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => startRename(tpl)}
                className="text-xs border-[#E1DFDD] rounded-sm"
                data-testid={`rename-template-${tpl.id}`}>
                <Type size={12} className="mr-1" /> Rename
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => duplicateTemplate(tpl.id)}
                className="text-xs border-[#E1DFDD] rounded-sm"
                data-testid={`duplicate-template-${tpl.id}`}>
                <Copy size={12} className="mr-1" /> Duplicate
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => setDeleteConfirm(tpl.id)}
                className="text-xs border-[#E1DFDD] rounded-sm text-[#A4262C] hover:bg-[#FDE7E9] hover:text-[#A4262C] hover:border-[#A4262C]/30 ml-auto"
                data-testid={`delete-template-${tpl.id}`}>
                <Trash2 size={12} className="mr-1" /> Delete
              </Button>
            </div>
            {deleteConfirm === tpl.id && (
              <div className="px-5 py-3 bg-[#FDE7E9] border-t border-[#A4262C]/20 flex items-center justify-between" data-testid={`delete-confirm-${tpl.id}`}>
                <span className="text-xs text-[#A4262C] font-medium">Delete "{tpl.name}" permanently?</span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setDeleteConfirm(null)}
                    className="text-xs bg-white text-[#5E5E5E] border border-[#E1DFDD] hover:bg-[#F3F2F1] rounded-sm h-7">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={() => deleteTemplate(tpl.id)}
                    className="text-xs bg-[#A4262C] text-white hover:bg-[#A4262C]/90 rounded-sm h-7"
                    data-testid={`confirm-delete-${tpl.id}`}>
                    Confirm Delete
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
